import { useState, useMemo, useEffect } from 'react';
import { useReactTable, getCoreRowModel, type ColumnDef } from '@tanstack/react-table';
import { Search, CheckCircle2, Package, Warehouse, Pencil, Eye, PackageOpen } from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { PageHeader } from '@/components/shared/page-header';
import { DataTable } from '@/components/data-table/data-table';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { StatusBadge } from '@/components/shared/status-badge';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useWarehouseOrders, useFulfillWarehouseOrder } from '@/hooks/use-warehouses';
import { useLocations } from '@/hooks/use-locations';
import { useLocationStore } from '@/stores/location-store';
import { useCanSeeCost } from '@/hooks/use-pos-config';
import { usePermissions } from '@/hooks/use-permissions';
import { formatCurrency, formatDateTime } from '@/lib/utils';
import { isWarehouseType } from '@/lib/location-type';
import { WAREHOUSE_STATUS_LABELS } from './warehouse-status';

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

export function WarehouseOutgoingListPage() {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const { selectedLocationId } = useLocationStore();
  const [warehouseLocationId, setWarehouseLocationId] = useState<string>('');
  const fulfillOrder = useFulfillWarehouseOrder();
  const canSeeCost = useCanSeeCost();
  const permissions = usePermissions();
  const canEdit = permissions.has('sales_orders.edit');
  const canFulfil = permissions.has('sales_orders.fulfil');
  const [fulfillTarget, setFulfillTarget] = useState<{ id: string; orderNumber: number } | null>(null);

  const { data: locationsData } = useLocations();
  const warehouseLocations = useMemo(
    () => (locationsData?.data ?? []).filter((l: any) => isWarehouseType(l.type) && l.isActive),
    [locationsData],
  );

  // Prefer the currently selected location (if it's a warehouse); otherwise first
  // warehouse. Follow changes to the header's location picker.
  useEffect(() => {
    if (warehouseLocations.length === 0) return;
    const selectedIsWarehouse = warehouseLocations.some((l: any) => l.id === selectedLocationId);
    if (selectedIsWarehouse && warehouseLocationId !== selectedLocationId) {
      setWarehouseLocationId(selectedLocationId ?? '');
    } else if (!warehouseLocationId) {
      setWarehouseLocationId(warehouseLocations[0].id);
    }
  }, [selectedLocationId, warehouseLocationId, warehouseLocations]);

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const { data, isLoading } = useWarehouseOrders({
    page,
    perPage,
    status: statusFilter || undefined,
    warehouseLocationId: warehouseLocationId || undefined,
  });

  const orders = data?.data ?? [];
  const pagination = data?.pagination;

  const handleFulfill = (id: string, orderNumber: number) => {
    setFulfillTarget({ id, orderNumber });
  };

  const confirmFulfill = async () => {
    if (!fulfillTarget) return;
    try {
      await fulfillOrder.mutateAsync(fulfillTarget.id);
      setFulfillTarget(null);
    } catch {
      // Error handled by global handler
      setFulfillTarget(null);
    }
  };

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'orderNumber',
      header: 'Order #',
      size: 70,
      cell: ({ row }) => <span className="font-medium text-primary">#{row.original.orderNumber}</span>,
    },
    {
      accessorKey: 'locationName',
      header: 'Destination',
      cell: ({ row }) => row.original.locationName || '—',
    },
    {
      accessorKey: 'warehouseLocationName',
      header: 'From Warehouse',
      cell: ({ row }) => row.original.warehouseLocationName || '—',
    },
    {
      accessorKey: 'createdByName',
      header: 'Ordered By',
      cell: ({ row }) => row.original.createdByName || 'System',
    },
    {
      accessorKey: 'purpose',
      header: 'Purpose',
      cell: ({ row }) =>
        row.original.purpose ? (
          <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-blue-700">
            {row.original.purpose}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    ...(canSeeCost
      ? [
          {
            accessorKey: 'totalCost',
            header: () => <div className="text-right">Total</div>,
            cell: ({ row }: any) => (
              <div className="text-right font-medium">{formatCurrency(row.original.totalCost)}</div>
            ),
          },
        ]
      : []),
    {
      accessorKey: 'status',
      header: 'Status',
      // See WAREHOUSE_STATUS_LABELS: the buyer's terminal 'received' reads as
      // "Received by buyer", matching the status filter.
      cell: ({ row }) => <StatusBadge status={row.original.status} labelMap={WAREHOUSE_STATUS_LABELS} />,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {formatDateTime(row.original.createdAt)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          {row.original.status === 'sent' && (
            <>
              <a
                href={`#/pos-management/sale-orders/${row.original.id}/edit`}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-background px-3 text-xs font-medium text-foreground hover:bg-accent"
                title={canEdit ? 'Edit quantities' : 'View this order (read-only)'}
              >
                {canEdit ? <Pencil className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {canEdit ? 'Edit' : 'View'}
              </a>
              {canFulfil && (
                <button
                  onClick={() => handleFulfill(row.original.id, row.original.orderNumber)}
                  disabled={fulfillOrder.isPending}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-3 text-xs font-medium text-primary hover:bg-primary/10 disabled:opacity-50"
                  title="Mark as Fulfilled"
                >
                  <Package className="h-3.5 w-3.5" />
                  Fulfill
                </button>
              )}
            </>
          )}
          {(row.original.status === 'fulfilled' ||
            row.original.status === 'received' ||
            row.original.status === 'completed') && (
            <>
              <a
                href={`#/pos-management/sale-orders/${row.original.id}/edit`}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-background px-3 text-xs font-medium text-foreground hover:bg-accent"
                title="View this order (read-only)"
              >
                <Eye className="h-3.5 w-3.5" />
                View
              </a>
              <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-medium text-green-700">
                <CheckCircle2 className="h-3 w-3" /> Fulfilled
              </span>
            </>
          )}
        </div>
      ),
    },
  ];

  // Client-side search across visible fields
  const filteredOrders = useMemo(() => {
    if (!search.trim()) return orders;
    const q = search.toLowerCase();
    return orders.filter(
      (o: any) =>
        String(o.orderNumber || '').includes(q) ||
        String(o.locationName || '')
          .toLowerCase()
          .includes(q) ||
        String(o.createdByName || '')
          .toLowerCase()
          .includes(q),
    );
  }, [orders, search]);

  const table = useReactTable({ data: filteredOrders, columns, getCoreRowModel: getCoreRowModel() });

  return (
    <div className="space-y-4">
      <PageHeader title="Sale Orders" description="Orders this warehouse is fulfilling for other locations" />

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Warehouse className="h-4 w-4 text-orange-600" />
          <select
            value={warehouseLocationId}
            onChange={(e) => {
              setWarehouseLocationId(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-lg border border-input bg-background px-3 text-sm"
          >
            {warehouseLocations.length === 0 && <option value="">No warehouse locations</option>}
            {warehouseLocations.map((l: any) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </div>

        <div className="relative min-w-0 flex-1 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search orders..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm"
        >
          <option value="">All Statuses</option>
          <option value="sent">In Progress</option>
          <option value="fulfilled">Fulfilled</option>
          <option value="received">Received by buyer</option>
        </select>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>
      ) : filteredOrders.length === 0 ? (
        <EmptyState
          icon={PackageOpen}
          title="No sale orders for this warehouse yet"
          description="Orders appear here when a location creates a PO against this warehouse as a vendor."
          className="h-64"
        />
      ) : (
        <>
          <DataTable table={table} columns={columns} />
          {pagination && (
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={PER_PAGE_OPTIONS}
              onPerPageChange={handlePerPageChange}
            />
          )}
        </>
      )}

      <ConfirmDialog
        open={!!fulfillTarget}
        title="Mark as Fulfilled?"
        message={fulfillTarget ? `Mark Order #${fulfillTarget.orderNumber} as fulfilled?` : ''}
        confirmLabel="Mark Fulfilled"
        variant="info"
        loading={fulfillOrder.isPending}
        onConfirm={confirmFulfill}
        onCancel={() => setFulfillTarget(null)}
      />
    </div>
  );
}
